/**
 * Item-type routing + display helpers.
 *
 * Every itemType has a URL segment under /brands/:brandSlug/ and a small
 * display label used on tile badges (so users can distinguish a plate tile
 * from a polish tile at a glance in mixed brand collections). Kept in one
 * place so adding a 9th itemType later means changing exactly this file
 * and one route registration in App.tsx.
 */
import type { ItemType } from '../types';

/**
 * URL path segment for an itemType. Hyphenated for multi-word types
 * (`cuticle_care` → `cuticle-care`) so URLs read naturally.
 */
export function itemTypeToPath(itemType: ItemType | undefined): string {
  switch (itemType) {
    case 'plate':
      return 'plate';
    case 'transfer':
      return 'transfer';
    case 'embellishment':
      return 'embellishment';
    case 'tool':
      return 'tool';
    case 'powder':
      return 'powder';
    case 'acrylic':
      return 'acrylic';
    case 'cuticle_care':
      return 'cuticle-care';
    case 'polish':
    default:
      return 'polish';
  }
}

/** Inverse — parse an itemType from a URL segment. */
export function pathToItemType(path: string): ItemType {
  switch (path) {
    case 'plate':
      return 'plate';
    case 'transfer':
      return 'transfer';
    case 'embellishment':
      return 'embellishment';
    case 'tool':
      return 'tool';
    case 'powder':
      return 'powder';
    case 'acrylic':
      return 'acrylic';
    case 'cuticle-care':
      return 'cuticle_care';
    case 'polish':
    default:
      return 'polish';
  }
}

/**
 * Display metadata for tile badges. Polish is the default type so it
 * intentionally has no badge — only non-polish tiles get one, keeping the
 * badge itself as the "not-a-polish" signal.
 */
export interface ItemTypeMeta {
  label: string;
  emoji: string;
}

export function itemTypeMeta(itemType: ItemType | undefined): ItemTypeMeta | null {
  switch (itemType) {
    case 'plate':
      return { label: 'Plate', emoji: '🎨' };
    case 'transfer':
      return { label: 'Transfer', emoji: '🖼️' };
    case 'embellishment':
      return { label: 'Embellishment', emoji: '✨' };
    case 'tool':
      return { label: 'Tool', emoji: '🛠️' };
    case 'powder':
      return { label: 'Powder', emoji: '🌀' };
    case 'acrylic':
      return { label: 'Acrylic', emoji: '💎' };
    case 'cuticle_care':
      return { label: 'Cuticle Care', emoji: '💧' };
    case 'polish':
    default:
      return null;
  }
}

/**
 * Same as itemTypeMeta but always returns a value — polish included.
 * Use this on surfaces that display type PILLS (e.g. brand listing
 * cards showing "polish, plate, tool") where the polish itself needs a
 * chip, not the "polish is default; skip its badge" behavior above.
 */
export function itemTypeMetaAlways(itemType: ItemType): ItemTypeMeta {
  if (itemType === 'polish') return { label: 'Polish', emoji: '💅' };
  return itemTypeMeta(itemType) ?? { label: itemType, emoji: '' };
}
